/**
 * Link a placed order to the chat message it was bought from. Called by
 * `ChatCheckoutDialog` after a successful PayPal capture so the conversation
 * thread reflects "order placed" against the originating offer/buy-now
 * message.
 *
 *   components/chat/ChatCheckoutDialog.tsx ─▶ linkOrderToMessage
 *                                                    │
 *                                                    ├── safeParse (boundary)
 *                                                    └── fetch + return
 *
 * Why this exists:
 *  - The inline fetch lived in `ChatCheckoutDialog.tsx::linkOrderToMessage`
 *    (G8 in `FRONTEND_WIRING_STRATEGY.md §3.2`). Extracted per §4 #3 so the
 *    component can be tested without `fetch` stubs and the request shape
 *    lives in `@kraahl/contract` alongside the rest of the chat-checkout
 *    surface.
 *  - When mobile chat checkout lands, the same `linkOrderToMessage` builder
 *    can be lifted — one contract, one fetch path, no parallel hardcoded URL.
 *
 * @see .tondocs/FRONTEND_WIRING_STRATEGY.md §4 #3 — pre-lock extract
 * @see packages/contract/src/chat-link-order.ts — request contract
 * @see app/api/chat/link-order/route.ts — handler
 */

import {
  LinkOrderToMessageRequestSchema,
  type LinkOrderToMessageRequest,
} from "@kraahl/contract";

// ---------------------------------------------------------------------------
// Shared error
// ---------------------------------------------------------------------------

/**
 * Thrown when the request body fails the contract Zod parse before fetch.
 * Programmer-bug signal: caller assembled args the contract does not accept.
 */
export class ChatLinkOrderContractError extends Error {
  readonly issues: ReadonlyArray<{ path: string; message: string }>;
  constructor(issues: ReadonlyArray<{ path: string; message: string }>) {
    super(
      `[chat/link-order] contract parse failed: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    );
    this.name = "ChatLinkOrderContractError";
    this.issues = issues;
  }
}

// ---------------------------------------------------------------------------
// linkOrderToMessage (G8)
// ---------------------------------------------------------------------------

export interface LinkOrderToMessageArgs {
  roomName: string;
  messageId: string;
  /** Human-readable `orders.order_number`. Server resolves to `orders.id`. */
  orderNumber: string;
}

export interface LinkOrderToMessageResult {
  /** `true` when the server linked the order to the message. */
  ok: boolean;
}

/**
 * Build the link-order request, schema-check it, POST it. Returns
 * `{ ok: response.ok }` — does NOT throw on non-2xx (the chat thread will
 * still render; the link is a nicety, not a money-path failure). Throws on
 * contract parse failure or network error.
 */
export async function linkOrderToMessage(
  args: LinkOrderToMessageArgs,
): Promise<LinkOrderToMessageResult> {
  const body: LinkOrderToMessageRequest = {
    roomName: args.roomName,
    messageId: args.messageId,
    orderNumber: args.orderNumber,
  };

  const parsed = LinkOrderToMessageRequestSchema.safeParse(body);
  if (!parsed.success) {
    throw new ChatLinkOrderContractError(
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    );
  }

  const response = await fetch("/api/chat/link-order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  return { ok: response.ok };
}
